import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged,
  reload,
} from "firebase/auth";
import { auth } from "./config";
import { USE_REAL_FIREBASE } from "./config";
import { createUserProfile } from "./firestore";
import { saveUserPreferences } from "./enhancedFirestoreService";

function mapAuthError(error) {
  const code = error?.code || "";
  switch (code) {
    case "auth/invalid-credential":
    case "auth/wrong-password":
    case "auth/user-not-found":
      return "Invalid email or password.";
    case "auth/email-already-in-use":
      return "An account with this email already exists.";
    case "auth/weak-password":
      return "Password must be at least 6 characters.";
    case "auth/too-many-requests":
      return "Too many attempts. Please try again later.";
    case "auth/network-request-failed":
      return "Network error. Check your connection and try again.";
    default:
      return error?.message || "Authentication failed. Please try again.";
  }
}

async function ensureUserFirestoreRecords(user, displayName) {
  const profileResult = await createUserProfile(user.uid, {
    email: user.email,
    displayName: displayName || user.displayName || user.email?.split("@")[0] || "",
  });

  if (!profileResult.success) {
    throw new Error(profileResult.error || "Failed to create user profile");
  }

  const prefsResult = await saveUserPreferences(user.uid, {
    marketCurrency: "USD",
    portfolioCurrency: "USD",
    isTtwror: false,
  });

  if (!prefsResult.success) {
    throw new Error(prefsResult.error || "Failed to save user preferences");
  }
}

export const signUp = async (email, password, displayName) => {
  try {
    if (!USE_REAL_FIREBASE) {
      console.log("Mock signUp successful:", email);
      return {
        success: true,
        user: {
          uid: `mock-${Date.now()}`,
          email,
          displayName: displayName || email.split("@")[0],
          emailVerified: false,
        },
      };
    }

    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    const resolvedDisplayName =
      displayName?.trim() || email.split("@")[0] || "User";

    if (resolvedDisplayName) {
      await updateProfile(user, { displayName: resolvedDisplayName });
    }

    await ensureUserFirestoreRecords(user, resolvedDisplayName);
    await sendEmailVerification(user);
    await signOut(auth);

    return {
      success: true,
      user,
      requiresEmailVerification: true,
      verificationEmailSent: true,
    };
  } catch (error) {
    console.error("Firebase signUp failed:", error);
    return { success: false, error: mapAuthError(error) };
  }
};

export const signIn = async (email, password) => {
  try {
    if (!USE_REAL_FIREBASE) {
      console.log("Mock signIn successful:", email);
      return {
        success: true,
        user: {
          uid: "mock-user-id",
          email,
          displayName: email.split("@")[0],
          emailVerified: true,
        },
      };
    }

    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    await reload(user);
    const refreshedUser = auth.currentUser || user;

    if (!refreshedUser?.emailVerified) {
      await signOut(auth);
      return {
        success: false,
        error:
          "Please verify your email before signing in. Check your inbox for the verification link.",
      };
    }

    return { success: true, user: refreshedUser };
  } catch (error) {
    console.error("Firebase signIn failed:", error);
    return { success: false, error: mapAuthError(error) };
  }
};

export const signOutUser = async () => {
  try {
    if (!USE_REAL_FIREBASE) {
      console.log("Mock signOut successful");
      return { success: true };
    }

    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error("Firebase signOut failed:", error);
    return { success: false, error: mapAuthError(error) };
  }
};

export const resetPassword = async (email) => {
  try {
    if (!USE_REAL_FIREBASE) {
      console.log("Mock resetPassword successful:", email);
      return { success: true };
    }

    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error) {
    console.error("Firebase resetPassword failed:", error);
    return { success: false, error: mapAuthError(error) };
  }
};

export const getCurrentUser = () => {
  if (!USE_REAL_FIREBASE) {
    return {
      uid: "mock-user-id",
      email: "mock@example.com",
      displayName: "Mock User",
      emailVerified: true,
    };
  }
  return auth.currentUser;
};

export const onAuthStateChange = (callback) => {
  if (!USE_REAL_FIREBASE) {
    return () => {};
  }
  return onAuthStateChanged(auth, callback);
};
